// settings.js — user preferences persisted to localStorage.
//
// Responsibilities:
//   - Expose a single source of truth for user-facing preferences
//     (locale, verbal-descriptions toggle).
//   - Persist changes to localStorage so they survive reloads.
//   - Tolerate a missing/broken localStorage (private browsing,
//     sandbox, disabled cookies) by silently falling back to an
//     in-memory store. The app MUST still be usable.
//   - Emit change events so UI components can react.

const STORAGE_KEYS = Object.freeze({
  locale: 'ihearewe.locale',
  verbalDescriptions: 'ihearewe.verbalDescriptions',
  shareStats: 'ihearewe.shareStats',
});

const DEFAULTS = Object.freeze({
  locale: 'en',
  verbalDescriptions: true,
  shareStats: true,
});

/**
 * Safe storage wrapper. Returns an object that mirrors localStorage's
 * getItem/setItem but never throws, even when the underlying store is
 * unavailable. Falls back to an in-memory Map.
 */
function createSafeStorage() {
  let store;
  try {
    // Some browsers throw on access to `localStorage` in sandboxed
    // contexts — not just on read/write.
    const testKey = '__ihearewe_test__';
    globalThis.localStorage.setItem(testKey, testKey);
    globalThis.localStorage.removeItem(testKey);
    store = globalThis.localStorage;
  } catch {
    const memory = new Map();
    store = {
      getItem: (k) => (memory.has(k) ? memory.get(k) : null),
      setItem: (k, v) => memory.set(k, String(v)),
      removeItem: (k) => memory.delete(k),
    };
  }
  return store;
}

const storage = createSafeStorage();

/** @type {Set<(state: ReturnType<typeof getAll>) => void>} */
const listeners = new Set();

function emit() {
  const snapshot = getAll();
  for (const fn of listeners) {
    try {
      fn(snapshot);
    } catch (err) {
      // A listener must never be able to break settings persistence.
      console.error('[settings] listener threw:', err);
    }
  }
}

// --- Locale ---------------------------------------------------------------

export function getLocale() {
  const raw = storage.getItem(STORAGE_KEYS.locale);
  return raw ?? DEFAULTS.locale;
}

/**
 * True when the user has an explicitly saved locale preference.
 * Returns false on a first-ever visit (or after localStorage wipe),
 * which tells bootstrap to run browser-language detection.
 */
export function hasStoredLocale() {
  return storage.getItem(STORAGE_KEYS.locale) !== null;
}

export function setLocale(value) {
  if (typeof value !== 'string' || value.length === 0) {
    return false;
  }
  const current = getLocale();
  if (current === value) return false;
  storage.setItem(STORAGE_KEYS.locale, value);
  emit();
  return true;
}

// --- Verbal descriptions --------------------------------------------------

export function getVerbalDescriptions() {
  const raw = storage.getItem(STORAGE_KEYS.verbalDescriptions);
  if (raw === null) return DEFAULTS.verbalDescriptions;
  return raw === 'true';
}

export function setVerbalDescriptions(value) {
  const next = Boolean(value);
  const current = getVerbalDescriptions();
  if (current === next) return false;
  storage.setItem(STORAGE_KEYS.verbalDescriptions, String(next));
  emit();
  return true;
}

// --- Share anonymous usage stats ------------------------------------------

export function getShareStats() {
  const raw = storage.getItem(STORAGE_KEYS.shareStats);
  if (raw === null) return DEFAULTS.shareStats;
  return raw === 'true';
}

export function setShareStats(value) {
  const next = Boolean(value);
  const current = getShareStats();
  if (current === next) return false;
  storage.setItem(STORAGE_KEYS.shareStats, String(next));
  emit();
  return true;
}

// --- Composite accessors --------------------------------------------------

export function getAll() {
  return {
    locale: getLocale(),
    verbalDescriptions: getVerbalDescriptions(),
    shareStats: getShareStats(),
  };
}

/**
 * Subscribe to settings changes. Returns an unsubscribe function.
 *
 * @param {(state: ReturnType<typeof getAll>) => void} fn
 */
export function onChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// --- Test helpers ---------------------------------------------------------

/** Reset state (in-memory and storage) back to defaults. Test-only. */
export function __resetForTests() {
  listeners.clear();
  for (const key of Object.values(STORAGE_KEYS)) {
    try {
      storage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
}

export const __STORAGE_KEYS = STORAGE_KEYS;
export const __DEFAULTS = DEFAULTS;
